import React from "react";
import { usePopoverState } from "reakit/Popover";
import styled from "styled-components";
import { content } from "@glossy";
import { Spacing } from "@glossy/spacings";
import Button from "@ui-kit/button";
import Icon from "@ui-kit/icon";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { usePinnedAuthorWidget } from "@radarly/core/widgets/use-cases";
import { PinnedAuthorCard } from "@radarly/models/cards/model";
import { Clause } from "@radarly/models/clause";
import { hardMergeClauses } from "@radarly/models/clause/merge";
import {
  FullInfluencer,
  InfluencerLensFactory,
} from "@radarly/models/influencer";
import Tile, { WrapperScopeActions } from "@radarly/shared/cards/tile";
import PinnedAuthor from "@radarly/shared/widgets/pinned-author";
import { CardProps } from "../model";

export const getPinnedAuthorClause = (
  authorId: string,
  baseClause?: Clause
): Clause => {
  if (!authorId) {
    return null;
  }

  const authorClause = {
    and: [{ query: `uid:"${authorId}"` }],
  };
  if (baseClause) {
    return hardMergeClauses(baseClause, authorClause);
  } else {
    return authorClause;
  }
};

const StyledButton = styled(Button)`
  margin-left: ${Spacing.Medium};
`;

const StyledIcon = styled(Icon)`
  margin-top: 2px;
  cursor: pointer;
  &:hover {
    color: ${(props) => content(props, "primary")};
  }
`;

const getAuthorPermalink = (author: FullInfluencer): string => {
  if (author) {
    const influencerLens = InfluencerLensFactory.create(author.user);
    return influencerLens.getPermalink();
  }
  return null;
};

const RadarlyPinnedAuthorCard = ({
  card,
  height,
  timezone,
  readOnly,
}: CardProps<PinnedAuthorCard>) => {
  const project = useCurrentProject();
  const locale = useCurrentUserLocale();
  const authorId = card.fragments[0].authorId;

  const { author, isLoading, isError } = usePinnedAuthorWidget(
    project.id,
    authorId
  );

  const permalink = getAuthorPermalink(author);
  const popover = usePopoverState({ placement: "top" });

  return (
    <Tile
      title={card.title}
      height={height}
      actions={
        permalink ? (
          <WrapperScopeActions
            className="wrapper-scope-actions"
            selected={popover.visible}
          >
            <StyledButton
              variant="tertiary"
              onClick={() => window.open(permalink, "_blank")}
            >
              <StyledIcon glyph="square-arrow" size={14} />
            </StyledButton>
          </WrapperScopeActions>
        ) : (
          <></>
        )
      }
    >
      <PinnedAuthor
        project={project}
        author={author}
        loading={isLoading}
        isError={isError}
        locale={locale}
        timezone={timezone}
        readOnly={readOnly}
      />
    </Tile>
  );
};

export default RadarlyPinnedAuthorCard;
